import { Box, Divider, Typography } from "@mui/joy";
import { Paper } from "../atoms/paper";
import { useMissionMap } from "../templates/projectInvite";

/**
 * Pay, as it appears on the call page.
 *
 * Three separate things arrive, and a project rarely has all three:
 *
 *   Wage             the project's free-text agreement line, with its unit
 *   WageDescription  terms a PERSON wrote — the union's H&W percentage, a side
 *                    agreement, who to ask
 *   WageBreakdown    what automatic wages compute from the labor and the scales:
 *                    scale, overscale, adjustment, expenses, union benefits
 *
 * The written terms lead and the computed breakdown sits under them, matching the
 * call email — a person stated the terms, the breakdown is only the arithmetic
 * behind them.
 *
 * The right-hand figure is dropped when there is no agreement line. It used to
 * render an empty <h4> beside a divider, which reads as a missing number rather
 * than as a project priced some other way.
 */
export function Wage({ token }: { token: string }) {
  const mission = useMissionMap(token);

  const hasAgreement = Boolean(mission?.Wage);

  return (
    <Paper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box sx={{ textAlign: "left" }}>
          <Typography
            startDecorator={<i className="fa-solid fa-wallet"></i>}
            level="title-lg"
          >
            Wage
          </Typography>
          {mission?.WageDescription ? (
            <Typography level="body-sm" sx={{ textAlign: "left", pt: 1 }}>
              {mission.WageDescription}
            </Typography>
          ) : (
            []
          )}
          {mission?.WageBreakdown ? (
            // HTML from the server, so the call page and the email cannot
            // disagree about what somebody is paid.
            //
            // Box, not Typography: Joy's Typography injects children for its
            // decorators, and React refuses children alongside
            // dangerouslySetInnerHTML (error #60).
            <Box
              sx={{ textAlign: "left", pt: 1, fontSize: 12 }}
              dangerouslySetInnerHTML={{ __html: mission.WageBreakdown }}
            />
          ) : (
            []
          )}
        </Box>
        {hasAgreement ? (
          <>
            <Divider orientation="vertical" />
            <Box sx={{ textAlign: "right" }}>
              <Typography
                level="h4"
                sx={{ fontWeight: 600, alignItems: "end" }}
              >
                {mission?.Wage}
              </Typography>
              {mission?.WageUnit ? (
                <Typography level="body-xs">{mission.WageUnit}</Typography>
              ) : (
                []
              )}
            </Box>
          </>
        ) : (
          []
        )}
      </Box>
    </Paper>
  );
}
